import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductImage from "@/components/ProductImage";
import { motion } from "framer-motion";
import { Plant, Package, CurrencyDollar, UploadSimple, Pencil, Trash, ChartLineUp, Truck, Tag, ShieldCheck, Warning } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const CATEGORIES = ["Pepper", "Cardamom", "Cinnamon", "Saffron", "Turmeric", "Cloves", "Nutmeg", "Star Anise", "Chilli", "Cumin", "Coffee Beans"];

export default function FarmerDashboard() {
  const { user, authHeaders, loading } = useAuth();
  const { format, code } = useCurrency();
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, o, a] = await Promise.all([
      axios.get(`${API}/farmer/products`, { headers: authHeaders(), withCredentials: true }),
      axios.get(`${API}/orders/farmer`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/analytics/farmer`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: null })),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setAnalytics(a.data);
  };

  useEffect(() => { if (user) load(); }, [user]);

  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await axios.delete(`${API}/products/${id}`, { headers: authHeaders(), withCredentials: true });
    toast.success("Deleted");
    load();
  };

  const ship = async (id) => {
    const tracking = window.prompt("Tracking number?");
    if (!tracking) return;
    await axios.post(`${API}/orders/${id}/ship`, { tracking_number: tracking }, { headers: authHeaders(), withCredentials: true });
    toast.success("Shipment recorded");
    load();
  };

  if (loading) return <div className="min-h-screen pt-32 text-center text-muted">Loading...</div>;
  if (!user || user.role !== "farmer") return <Redirect />;

  const kycOk = !!user.kyc_path;

  return (
    <div className="min-h-screen pt-24 bg-ink-950" data-testid="farmer-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="section-label">Farmer dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Welcome, {user.name?.split(" ")[0]}.</h1>
            <p className="text-muted mt-1">{user.company} · {user.country}</p>
          </div>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="add-product-btn">
            <Plant size={16} weight="bold" /> Add product
          </button>
        </div>

        {/* KYC banner */}
        <KycBanner user={user} ok={kycOk} />

        <div className="mt-8 flex gap-2 border-b border-white/5">
          {[
            { id: "overview", label: "Overview" },
            { id: "products", label: "Stock" },
            { id: "orders", label: "Orders" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm border-b-2 transition-colors ${tab === t.id ? "border-saffron text-cream" : "border-transparent text-muted hover:text-cream"}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            <StatCard Icon={Package} label="My products" val={analytics?.products_count ?? products.length} />
            <StatCard Icon={Truck} label="Orders" val={analytics?.orders_count ?? 0} />
            <StatCard Icon={CurrencyDollar} label={`Revenue (30d) · ${code}`} val={format(analytics?.total_30d ?? 0)} />
            <StatCard Icon={ChartLineUp} label="Active listings" val={products.filter((p) => p.stock_kg > 0).length} />
            <div className="md:col-span-4 glass rounded-2xl p-6 mt-2">
              <h3 className="font-display text-2xl mb-4">Revenue · last 30 days</h3>
              <div className="h-[240px]">
                <ResponsiveContainer>
                  <AreaChart data={analytics?.series || []}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F4A261" stopOpacity={0.7} />
                        <stop offset="100%" stopColor="#F4A261" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: "#A3A39C", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fill: "#A3A39C", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#0A1410", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#F4A261" fill="url(#rev)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div className="mt-10">
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ink-800/60">
                  <tr className="text-left text-muted uppercase tracking-[0.2em] text-[10px]">
                    <th className="px-5 py-4">Product</th>
                    <th className="px-5 py-4">Grade</th>
                    <th className="px-5 py-4">Stock</th>
                    <th className="px-5 py-4">Price</th>
                    <th className="px-5 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-white/5" data-testid={`row-${p.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {p.images?.[0] && <ProductImage src={p.images[0]} alt={p.name} className="w-12 h-12 rounded-lg object-cover" />}
                          <div>
                            <div className="text-cream">{p.name}</div>
                            <div className="text-[11px] text-muted">{p.country} · {p.category}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4"><span className="chip">Grade {p.grade}</span></td>
                      <td className="px-5 py-4 text-cream">{p.stock_kg} kg</td>
                      <td className="px-5 py-4 text-saffron">{format(p.price_usd)}</td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 border border-white/10 rounded-full hover:border-saffron" data-testid={`edit-${p.id}`}><Pencil size={14} /></button>
                          <button onClick={() => del(p.id)} className="p-2 border border-white/10 rounded-full hover:border-red-500 hover:text-red-500" data-testid={`del-${p.id}`}><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted py-12">No products yet. Add your first listing.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-10 space-y-4">
            {orders.length === 0 && <div className="glass rounded-2xl p-16 text-center text-muted">No orders assigned yet.</div>}
            {orders.map((o) => (
              <div key={o.id} className="glass rounded-2xl p-5" data-testid={`order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Order #{o.id.slice(0, 8)}</div>
                    <h3 className="font-display text-xl text-cream mt-1">{o.items.length} item(s) · Anonymous buyer</h3>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-saffron">{format(o.items.reduce((s, it) => s + it.line_total, 0))}</div>
                    <span className="chip mt-1 inline-block">{o.status}</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted space-y-1">
                  {o.items.map((it, i) => <div key={i}>• {it.branded_name} — {it.qty_kg} kg</div>)}
                </div>
                <div className="mt-5 flex gap-3 flex-wrap">
                  {o.status !== "shipped" && o.status !== "delivered" && (
                    <button onClick={() => ship(o.id)} className="btn-primary text-sm" data-testid={`ship-${o.id}`}><Truck size={14} /> Mark shipped</button>
                  )}
                  <a href={`${API}/orders/${o.id}/label`} target="_blank" rel="noreferrer" className="btn-secondary text-sm"><Tag size={14} /> Print label</a>
                  {o.tracking_number && <span className="text-sm text-cream/80 self-center">Tracking: {o.tracking_number}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && <ProductForm product={editing} onClose={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function StatCard({ Icon, label, val }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
      <Icon size={22} weight="duotone" className="text-saffron" />
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted mt-3">{label}</div>
      <div className="font-display text-3xl text-cream mt-1">{val}</div>
    </motion.div>
  );
}

function Redirect() {
  React.useEffect(() => { window.location.href = "/login"; }, []);
  return null;
}

function KycBanner({ user, ok }) {
  const { authHeaders, checkAuth } = useAuth();
  const fileRef = React.useRef();
  const [busy, setBusy] = React.useState(false);
  const [idNumber, setIdNumber] = React.useState(user?.kyc_id_number || "");
  const [idType, setIdType] = React.useState(user?.kyc_id_type || "aadhaar");
  const [expanded, setExpanded] = React.useState(!ok);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await axios.post(`${API}/auth/kyc`, { kyc_path: r.data.path, id_type: idType, id_number: idNumber || "pending" },
        { headers: authHeaders(), withCredentials: true });
      await checkAuth();
      toast.success("KYC document submitted");
    } catch (e) {
      toast.error("Upload failed");
    }
    setBusy(false);
  };

  if (ok && !expanded) {
    return (
      <div className="mt-6 glass rounded-2xl px-5 py-3 flex items-center justify-between" data-testid="kyc-banner-ok">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} weight="duotone" className="text-forest" />
          <div>
            <div className="text-sm text-cream">KYC verified</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted">{user.kyc_id_type?.toUpperCase() || "ID"} on file</div>
          </div>
        </div>
        <button onClick={() => setExpanded(true)} className="text-xs text-gold hover:text-saffron">Update</button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mt-6 glass rounded-2xl p-6 border ${ok ? "border-white/10" : "border-saffron/40"}`}
      data-testid="kyc-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        {ok ? (
          <ShieldCheck size={28} weight="duotone" className="text-forest" />
        ) : (
          <Warning size={28} weight="duotone" className="text-saffron" />
        )}
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-display text-2xl text-cream">{ok ? "Update your KYC" : "Complete your KYC to list products"}</h3>
          <p className="text-sm text-muted mt-1 max-w-2xl">
            Upload a government-issued ID (Aadhaar / PAN / Passport). Verified farmers earn the "Verified Origin" badge and 2x higher buyer trust.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <select className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-cream" value={idType} onChange={(e) => setIdType(e.target.value)} data-testid="farmer-kyc-type">
              <option value="aadhaar">Aadhaar (India)</option>
              <option value="pan">PAN (India)</option>
              <option value="passport">Passport</option>
              <option value="national_id">National ID</option>
            </select>
            <input className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-cream" placeholder="ID number" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} data-testid="farmer-kyc-number" />
          </div>
          <div className="mt-4 flex gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-primary text-sm" data-testid="farmer-kyc-upload">
              <UploadSimple size={14} weight="bold" />
              {busy ? "Uploading..." : ok ? "Replace document" : "Upload document"}
            </button>
            {ok && (
              <button onClick={() => setExpanded(false)} className="btn-ghost text-sm">Cancel</button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ProductForm({ product, onClose }) {
  const { authHeaders } = useAuth();
  const fileRef = useRef();
  const [form, setForm] = useState(
    product || {
      name: "", category: "Pepper", parent_category: "Spices", country: "", grade: "A",
      stock_kg: 100, price_usd: 10, description: "", images: [], certifications: [], organic: false, fairtrade: false, expiry: "", shipping_terms: "FOB Origin",
    }
  );
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const uploadFile = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const r = await axios.post(`${API}/upload`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" }, withCredentials: true });
    return `${API}/files/${r.data.path}`;
  };

  const onFiles = async (e) => {
    setUploading(true);
    const files = Array.from(e.target.files || []);
    try {
      const urls = await Promise.all(files.map(uploadFile));
      update("images", [...form.images, ...urls]);
      toast.success(`Uploaded ${urls.length} file(s)`);
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (product) {
        await axios.put(`${API}/products/${product.id}`, form, { headers: authHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/products`, form, { headers: authHeaders(), withCredentials: true });
      }
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="product-form">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-strong rounded-3xl w-full max-w-3xl p-8 my-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-3xl">{product ? "Edit" : "Add"} product</h2>
          <button onClick={onClose} className="btn-ghost text-2xl">×</button>
        </div>
        <form onSubmit={save} className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
          <Field label="Name" value={form.name} onChange={(v) => update("name", v)} required testid="form-name" />
          <div>
            <label className="section-label block mb-2">Category</label>
            <select className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-cream" value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="form-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <Field label="Country" value={form.country} onChange={(v) => update("country", v)} required testid="form-country" />
          <div>
            <label className="section-label block mb-2">Grade</label>
            <select className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-cream" value={form.grade} onChange={(e) => update("grade", e.target.value)} data-testid="form-grade">
              {["A", "B", "C"].map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <Field label="Stock (kg)" type="number" value={form.stock_kg} onChange={(v) => update("stock_kg", parseFloat(v))} testid="form-stock" />
          <Field label="Price USD/kg" type="number" value={form.price_usd} onChange={(v) => update("price_usd", parseFloat(v))} testid="form-price" />
          <Field label="Expiry" type="date" value={form.expiry || ""} onChange={(v) => update("expiry", v)} />
          <Field label="Shipping terms" value={form.shipping_terms || ""} onChange={(v) => update("shipping_terms", v)} />
          <div className="md:col-span-2">
            <label className="section-label block mb-2">Description</label>
            <textarea className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-cream min-h-[90px]" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="form-description" />
          </div>
          <div className="md:col-span-2 flex gap-6">
            <label className="flex items-center gap-2 text-cream/80"><input type="checkbox" checked={form.organic} onChange={(e) => update("organic", e.target.checked)} /> Organic</label>
            <label className="flex items-center gap-2 text-cream/80"><input type="checkbox" checked={form.fairtrade} onChange={(e) => update("fairtrade", e.target.checked)} /> Fairtrade</label>
          </div>
          <div className="md:col-span-2">
            <label className="section-label block mb-2">Product images (5+ recommended)</label>
            <div className="flex gap-2 flex-wrap mb-3">
              {form.images.map((src, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10">
                  <ProductImage src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => update("images", form.images.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-ink-950/80 w-5 h-5 rounded-full text-xs">×</button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-muted hover:border-saffron hover:text-saffron" data-testid="upload-btn">
                {uploading ? "..." : <UploadSimple size={20} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
            </div>
          </div>
          <div className="md:col-span-2 flex justify-end gap-3 mt-4">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="save-product">Save product</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="section-label block mb-2">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-cream focus:border-saffron outline-none" />
    </div>
  );
}
